import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, CalendarClock, Megaphone, MessageSquare, Clock, Users, Zap, TrendingDown, CheckCircle, Smartphone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const ViberIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
    <path
      d="M20 12.5C20 16.5 17 20 12 20c-.6 0-1.2-.1-1.7-.2L7 22l.3-3.2C5.6 17.5 4 15.2 4 12.5 4 8.4 7 5 12 5s8 3.4 8 7.5z"
      fill="currentColor"
      fillOpacity="0.12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 11.5c.5 1.5 1.5 2.5 3 3M9 9.5c1.2 2.5 3.5 4.5 6 5.5M9 13.5c1 1.5 2.5 2.5 4 3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const useCases = [
  {
    title: "Admission Alerts",
    description: "Notify prospective students of application deadlines, missing documents, or acceptance decisions.",
    Icon: Bell,
    color: "text-amber",
    tags: ["SMS", "Viber"],
  },
  {
    title: "Enrollment Reminders",
    description: "Reduce drop-off with timely reminders about tuition deadlines and next steps.",
    Icon: CalendarClock,
    color: "text-sky",
    tags: ["SMS"],
  },
  {
    title: "Event Announcements",
    description: "Promote campus events, guest lectures, or open days with bulk messaging.",
    Icon: Megaphone,
    color: "text-lime",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Class & Schedule Updates",
    description: "Notify students instantly of cancellations, reschedules, or campus closures.",
    Icon: Clock,
    color: "text-violet",
    tags: ["SMS", "Viber"],
  },
  {
    title: "Student Engagement Campaigns",
    description: "Send personalized follow-ups and course promotions to prospective and current students.",
    Icon: Users,
    color: "text-pink",
    tags: ["SMS", "RCS"],
  },
];

const enhancedBenefits = [
  {
    title: "Reach Students Where They Are",
    description: "Connect with prospective and current students on the messaging channels they use every day.",
    Icon: Smartphone,
    color: "text-amber",
  },
  {
    title: "Reduce Application Drop-Off",
    description: "Timely reminders and status updates keep applicants moving through enrollment.",
    Icon: TrendingDown,
    color: "text-sky",
  },
  {
    title: "Automate Every Stage of the Journey",
    description: "Trigger admission, enrollment, and event messages based on student behavior and milestones.",
    Icon: Zap,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Reach students reliably for alerts and reminders.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "Viber Business Messages", description: "Send rich application and event updates.", Icon: MessageSquare, gradient: "icon-tile-indigo" },
  { title: "RCS", description: "Deliver interactive engagement messaging.", Icon: Megaphone, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is SMS messaging used for in education marketing?", answer: "Educational institutions use SMS to send admission alerts, enrollment reminders, event announcements, and schedule updates — helping reach students quickly with time-sensitive information." },
  { question: "Is texting students for marketing purposes legal?", answer: "Yes, when done in compliance with regulations like the TCPA and GDPR. Students must give clear consent to receive messages, especially for marketing or non-emergency communications, and opt-out options must always be included." },
  { question: "Can institutions send application status updates automatically?", answer: "Yes, application stage changes can trigger automated SMS or Viber notifications to applicants." },
  { question: "What channels work best for reaching students?", answer: "SMS offers the broadest reach, while Viber and RCS support richer messaging for event invites and campus updates." },
  { question: "Is this suitable for ongoing student engagement, not just admissions?", answer: "Yes, the same messaging infrastructure supports event notifications, schedule changes, and general student communication throughout the academic year." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "Viber", Icon: ViberIcon },
  { label: "RCS", Icon: Megaphone },
];

const IndustriesEducationMarketing = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="Student Engagement & Application Alerts – Yootelco"
        description="Attract, engage, and retain students with targeted messaging. Send enrollment reminders, application alerts, and campus updates with Yootelco."
        path="/industries/education-marketing"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Reach Students Where They Are
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for Education Marketing
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Student engagement messaging and automated application status alerts, built for schools, universities, EdTech companies, and training providers. Yootelco helps institutions keep prospective and current students informed at every stage.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Education Marketers Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Education Marketers Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in digital communications, we help educational institutions, EdTech companies, and training providers attract, engage, and retain students through targeted marketing and communication solutions. Whether you're promoting new courses, managing student recruitment campaigns, sending enrollment reminders, or nurturing prospective students throughout the admissions process, our solutions are designed to maximize engagement and conversions.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            From lead generation and personalized messaging to automated follow-ups, omnichannel campaigns, and student support communications, we help education providers optimize every stage of the journey.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">How Institutions Use Yootelco</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for Education Marketing</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Automated messaging for admissions and enrollment",
              "Multi-channel delivery across SMS, Viber, and RCS",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us about your institution's messaging needs, we'll show you how Yootelco can support student engagement and admissions."
        useCase="Education Marketing"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Reach Your Students?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about engagement messaging and application alerts for your institution.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesEducationMarketing;
